import { z } from 'zod';

// src/version.ts
var PROTOCOL_VERSION = 4;
var REPLAY_FORMAT_VERSION = 1;
var cardSchema = z.number().int().min(0).max(51);
var seatSchema = z.number().int().min(0).max(3);
var teamSchema = z.number().int().min(0).max(3);
var viewerSchema = z.union([seatSchema, z.literal("spectator")]);
var variantSchema = z.enum(["standard", "cross"]);
var playerCountSchema = z.union([z.literal(2), z.literal(3), z.literal(4)]);
var targetScoreSchema = z.union([z.literal(121), z.literal(61), z.literal(31)]);
var gameConfigSchema = z.object({
  variant: variantSchema,
  playerCount: playerCountSchema,
  teams: z.boolean(),
  targetScore: targetScoreSchema,
  /**
   * The Cross-Cribbage dealer's crib, a game option that is off by default.
   *
   * Defaulted rather than required so a config stored before the option
   * existed still parses as the game it was: a grid with no crib.
   */
  crib: z.boolean().default(false)
});
var phaseSchema = z.enum([
  "cutForDeal",
  "discard",
  "pegging",
  "show",
  "placing",
  "gridScoring",
  "gameOver"
]);
var moveSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("discard"), cards: z.array(cardSchema).min(1).max(2).readonly() }),
  z.object({ type: z.literal("play"), card: cardSchema }),
  z.object({ type: z.literal("place"), cell: z.number().int().min(0).max(24) }),
  /** Leaving the table. Carries nothing: the seat comes from the envelope. */
  z.object({ type: z.literal("forfeit") })
]);
var engineOutcomeSchema = z.enum(["completed", "forfeited"]);
var scoreReasonSchema = z.enum([
  "fifteen",
  "pair",
  "run",
  "flush",
  "nobs",
  "his_heels",
  "thirty_one",
  "go",
  "last_card",
  "margin"
]);
var scoreItemSchema = z.object({
  reason: scoreReasonSchema,
  points: z.number().int().min(0),
  cards: z.array(cardSchema).readonly()
});
var scoreSourceSchema = z.enum(["pegging", "hand", "crib", "heels", "grid"]);
var playedCardSchema = z.object({ seat: seatSchema, card: cardSchema });
var crossLineSchema = z.object({
  kind: z.enum(["row", "column"]),
  index: z.number().int().min(0).max(4)
});
var crossStateSchema = z.object({
  board: z.array(cardSchema.nullable()).length(25).readonly(),
  turn: seatSchema,
  /** `null` for every viewer but the seat on move once the crib is on. */
  drawn: cardSchema.nullable(),
  gridNumber: z.number().int().min(1),
  /**
   * Cards each seat has sent to the crib this grid, one entry per seat. All
   * zeros with the option off.
   *
   * The default is zeros rather than an empty array. A frame written before the
   * crib existed is a game with no throws, not a game with no seats, and an
   * empty array would hand a screen `undefined` for every seat it asked about.
   * The two-entry default only stands in for a frame that predates the crib;
   * a live game sends one entry per seat. `throwsBySeat` in `@cribbage/engine`
   * is the safe read either way.
   *
   * The ceiling is loose on purpose. The largest quota any format can ask for is
   * two — `throwQuota(config, seat)` is two at two players and for the player
   * alone at three, and one otherwise — so four leaves room for a rule change
   * without a wire bump, while still refusing the garbage a missing bound would
   * let through.
   */
  throws: z.array(z.number().int().min(0).max(4)).readonly().default([0, 0])
});
var redactedPeggingSchema = z.object({
  count: z.number().int().min(0).max(31),
  stack: z.array(cardSchema).readonly(),
  history: z.array(playedCardSchema).readonly(),
  turn: seatSchema,
  saidGo: z.array(z.boolean()).readonly(),
  lastPlayer: seatSchema.nullable()
});
var opponentViewSchema = z.object({
  seat: seatSchema,
  team: teamSchema,
  handCount: z.number().int().min(0).max(6),
  discarded: z.boolean()
});
var seatResultSchema = z.object({
  seat: seatSchema,
  team: teamSchema,
  score: z.number().int().min(0),
  placement: z.number().int().min(1).max(4),
  won: z.boolean(),
  skunked: z.boolean(),
  doubleSkunked: z.boolean(),
  /** True for every seat on the side that forfeited; in partners, both of them. */
  forfeited: z.boolean(),
  stats: z.record(z.string(), z.number())
});
var gameResultSchema = z.object({
  winner: teamSchema,
  scores: z.array(z.number().int().min(0)).readonly(),
  seats: z.array(seatResultSchema).readonly(),
  handsPlayed: z.number().int().min(1),
  outcome: engineOutcomeSchema,
  /** The seat that forfeited, or null for a game played out. */
  forfeitedBy: seatSchema.nullable()
});
var redactedStateSchema = z.object({
  config: gameConfigSchema,
  engineVersion: z.string(),
  viewer: viewerSchema,
  seq: z.number().int().min(0),
  phase: phaseSchema,
  handNumber: z.number().int().min(1),
  dealer: seatSchema,
  scores: z.array(z.number().int().min(0)).readonly(),
  prevScores: z.array(z.number().int().min(0)).readonly(),
  deckCount: z.number().int().min(0).max(52),
  hand: z.array(cardSchema).readonly(),
  opponents: z.array(opponentViewSchema).readonly(),
  cribCount: z.number().int().min(0).max(4),
  ownDiscards: z.array(cardSchema).readonly(),
  crib: z.array(cardSchema).readonly().nullable(),
  starter: cardSchema.nullable(),
  pegging: redactedPeggingSchema.nullable(),
  cross: crossStateSchema.nullable(),
  /**
   * Three-player Cross's draw for who plays alone. Defaulted so a frame from
   * before the draw existed parses as the game it was: one with no draw.
   */
  roleDraw: z.array(playedCardSchema).readonly().nullable().default(null),
  awaitingSeats: z.array(seatSchema).readonly(),
  winner: teamSchema.nullable(),
  result: gameResultSchema.nullable()
});
var withSeq = { seq: z.number().int().min(0) };
var gameEventSchema = z.discriminatedUnion("type", [
  z.object({ ...withSeq, type: z.literal("GAME_STARTED"), config: gameConfigSchema }),
  z.object({
    ...withSeq,
    type: z.literal("ROLES_DRAWN"),
    cuts: z.array(playedCardSchema).readonly(),
    alone: seatSchema
  }),
  z.object({
    ...withSeq,
    type: z.literal("CUT_FOR_DEAL"),
    cuts: z.array(playedCardSchema).readonly(),
    dealer: seatSchema
  }),
  z.object({
    ...withSeq,
    type: z.literal("HAND_STARTED"),
    handNumber: z.number().int().min(1),
    dealer: seatSchema
  }),
  z.object({
    ...withSeq,
    type: z.literal("CARDS_DEALT"),
    seat: seatSchema,
    cards: z.array(cardSchema).readonly(),
    count: z.number().int().min(0).max(6)
  }),
  z.object({ ...withSeq, type: z.literal("CRIB_SEEDED"), card: cardSchema.nullable() }),
  z.object({
    ...withSeq,
    type: z.literal("DISCARDED"),
    seat: seatSchema,
    cards: z.array(cardSchema).readonly().nullable(),
    count: z.number().int().min(1).max(2)
  }),
  z.object({ ...withSeq, type: z.literal("ALL_DISCARDED") }),
  z.object({
    ...withSeq,
    type: z.literal("STARTER_CUT"),
    card: cardSchema,
    dealer: seatSchema
  }),
  z.object({
    ...withSeq,
    type: z.literal("CARD_PLAYED"),
    seat: seatSchema,
    card: cardSchema,
    count: z.number().int().min(0).max(31)
  }),
  z.object({ ...withSeq, type: z.literal("GO_DECLARED"), seat: seatSchema }),
  z.object({
    ...withSeq,
    type: z.literal("POINTS_SCORED"),
    seat: seatSchema,
    team: teamSchema,
    item: scoreItemSchema,
    total: z.number().int().min(0),
    source: scoreSourceSchema
  }),
  z.object({ ...withSeq, type: z.literal("COUNT_RESET") }),
  z.object({ ...withSeq, type: z.literal("PEGGING_ENDED") }),
  z.object({
    ...withSeq,
    type: z.literal("HAND_SHOWN"),
    seat: seatSchema,
    cards: z.array(cardSchema).readonly(),
    starter: cardSchema,
    items: z.array(scoreItemSchema).readonly(),
    total: z.number().int().min(0)
  }),
  z.object({
    ...withSeq,
    type: z.literal("CRIB_SHOWN"),
    seat: seatSchema,
    cards: z.array(cardSchema).readonly(),
    starter: cardSchema,
    items: z.array(scoreItemSchema).readonly(),
    total: z.number().int().min(0)
  }),
  z.object({ ...withSeq, type: z.literal("PHASE_CHANGED"), phase: phaseSchema }),
  z.object({
    ...withSeq,
    type: z.literal("CARD_DRAWN"),
    seat: seatSchema,
    /** Redacted to `null` for every viewer but the drawing seat with the crib on. */
    card: cardSchema.nullable()
  }),
  z.object({
    ...withSeq,
    type: z.literal("CARD_PLACED"),
    seat: seatSchema.nullable(),
    cell: z.number().int().min(0).max(24),
    card: cardSchema
  }),
  z.object({
    ...withSeq,
    type: z.literal("LINE_COMPLETED"),
    line: crossLineSchema,
    team: teamSchema,
    cells: z.array(z.number().int().min(0).max(24)).readonly(),
    items: z.array(scoreItemSchema).readonly(),
    total: z.number().int().min(0)
  }),
  z.object({
    ...withSeq,
    type: z.literal("GRID_COMPLETED"),
    gridNumber: z.number().int().min(1),
    /** Line points per team — rows first, columns second. */
    totals: z.array(z.number().int().min(0)).readonly(),
    /**
     * Two to the dealing side for a Jack Cut Card, already counted inside
     * `totals`. Carried on its own because it belongs to no line, so a client
     * walking the ten lines would otherwise come up two short of the total it
     * is explaining. `null` unless the Cut Card is a Jack.
     */
    // Old queued results and stored replays predate this field.
    hisHeels: z.object({ team: teamSchema, item: scoreItemSchema }).nullable().default(null),
    margin: z.number().int().min(0),
    winner: teamSchema.nullable(),
    /**
     * The twenty-five cards as the grid ended; `null` for an empty cell.
     *
     * Bounded like `crossStateSchema.board`, which is the same twenty-five
     * cells. Without the length an array of one validates and reaches the
     * store, where the match walk draws a finished grid with empty cells.
     */
    board: z.array(cardSchema.nullable()).length(25).readonly(),
    /**
     * The dealer's crib, counted last for the dealing side with the Cut Card as
     * its fifth, and already inside `totals`. `null` whenever the option is off.
     */
    // Old queued results and stored replays predate this field.
    crib: z.object({
      cards: z.array(cardSchema).length(4).readonly(),
      items: z.array(scoreItemSchema).readonly(),
      total: z.number().int().min(0),
      team: teamSchema
    }).nullable().default(null)
  }),
  z.object({ ...withSeq, type: z.literal("GAME_ENDED"), result: gameResultSchema })
]);
var TALK_PHRASE_IDS = [
  "good_luck",
  "nice_hand",
  "well_played",
  "thanks",
  "close_one",
  "good_game"
];
var TALK_EMOJI_IDS = [
  "joy",
  "tongue_out",
  "silly",
  "excited",
  "cry",
  "surprised",
  "eyes",
  "heart",
  "thumbs_up",
  "skunk"
];
var PREMIUM_EMOTE_IDS = [
  "nana_shades",
  "nana_tea",
  "nana_wink",
  "nana_tears",
  "sarge_rage",
  "sarge_salute",
  "sarge_sob",
  "sarge_medal",
  "diva_gasp",
  "diva_swoon",
  "diva_kiss",
  "diva_rose",
  "pixel_cheer",
  "pixel_brain",
  "pixel_sweat",
  "skunk_stink",
  "skunk_bow",
  "skunk_nap",
  "peg_flag",
  "peg_fire",
  "peg_rocket",
  "peg_boxers",
  "his_nobs",
  "perfect_hand",
  "empty_hand",
  "crib_treasure",
  "crib_cobweb",
  "lucky_cut",
  "fist_bump",
  "muggins"
];
var PREMIUM = new Set(PREMIUM_EMOTE_IDS);
function isPremiumEmote(id) {
  return PREMIUM.has(id);
}
var EMOTE_IDS = [...TALK_PHRASE_IDS, ...TALK_EMOJI_IDS, ...PREMIUM_EMOTE_IDS];
var emoteIdSchema = z.enum(EMOTE_IDS);
var errorCodeSchema = z.enum([
  "BAD_MESSAGE",
  "BAD_TOKEN",
  "PROTOCOL_MISMATCH",
  "NOT_YOUR_SEAT",
  "ILLEGAL_MOVE",
  "RATE_LIMITED",
  /**
   * A Table talk send inside the sender's cooldown. Separate from
   * `RATE_LIMITED`, which can mean a dropped move and has to stay visible:
   * this one the client handles quietly.
   */
  "TALK_COOLDOWN",
  /** A members' emote from a seat with no membership. Handled as quietly. */
  "TALK_LOCKED",
  "ROOM_CLOSED",
  "INTERNAL"
]);
var seatStatusSchema = z.enum(["connected", "disconnected", "bot"]);
var c2sMoveSchema = z.object({
  type: z.literal("move"),
  /** The `seq` the client believed the room was at when it decided. */
  seq: z.number().int().min(0),
  move: moveSchema
});
var c2sResyncSchema = z.object({ type: z.literal("resync") });
var c2sEmoteSchema = z.object({ type: z.literal("emote"), id: emoteIdSchema });
var c2sReclaimSeatSchema = z.object({ type: z.literal("reclaim_seat") });
var c2sPingSchema = z.object({ type: z.literal("ping"), t: z.number() });
var c2sReadySchema = z.object({ type: z.literal("ready") });
var clientMessageSchema = z.discriminatedUnion("type", [
  c2sMoveSchema,
  c2sResyncSchema,
  c2sEmoteSchema,
  c2sReclaimSeatSchema,
  c2sPingSchema,
  c2sReadySchema
]);
var s2cSnapshotSchema = z.object({
  type: z.literal("snapshot"),
  seq: z.number().int().min(0),
  protocolVersion: z.number().int(),
  seat: seatSchema.nullable(),
  view: redactedStateSchema,
  seatStatus: z.array(seatStatusSchema),
  /** Wall-clock deadline for the seat on turn, or null when no timer runs. */
  deadline: z.number().int().nullable()
});
var s2cEventsSchema = z.object({
  type: z.literal("events"),
  seq: z.number().int().min(0),
  events: z.array(gameEventSchema),
  view: redactedStateSchema
});
var s2cMoveRejectedSchema = z.object({
  type: z.literal("move_rejected"),
  seq: z.number().int().min(0),
  code: z.string(),
  reason: z.string().optional()
});
var s2cTimerSchema = z.object({
  type: z.literal("timer"),
  seat: seatSchema,
  /** Absolute epoch ms, so clients render a countdown off their own clock. */
  deadline_ts: z.number().int()
});
var s2cSeatStatusSchema = z.object({
  type: z.literal("seat_status"),
  seat: seatSchema,
  status: seatStatusSchema
});
var s2cEmoteSchema = z.object({
  type: z.literal("emote"),
  seat: seatSchema,
  id: emoteIdSchema
});
var s2cGameOverSchema = z.object({
  type: z.literal("game_over"),
  result: gameResultSchema,
  ranked: z.boolean()
});
var s2cErrorSchema = z.object({
  type: z.literal("error"),
  code: errorCodeSchema,
  message: z.string().optional()
});
var s2cPongSchema = z.object({ type: z.literal("pong"), t: z.number() });
var serverMessageSchema = z.discriminatedUnion("type", [
  s2cSnapshotSchema,
  s2cEventsSchema,
  s2cMoveRejectedSchema,
  s2cTimerSchema,
  s2cSeatStatusSchema,
  s2cEmoteSchema,
  s2cGameOverSchema,
  s2cErrorSchema,
  s2cPongSchema
]);
var joinOptionsSchema = z.object({
  token: z.string().min(1),
  protocolVersion: z.number().int()
});
function currentJoinOptions(token) {
  return { token, protocolVersion: PROTOCOL_VERSION };
}
function parseClientMessage(raw) {
  const parsed = clientMessageSchema.safeParse(raw);
  return parsed.success ? { ok: true, message: parsed.data } : {
    ok: false,
    error: parsed.error.issues.map((i) => `${i.path.join(".")}: ${i.message}`).join("; ")
  };
}
function parseServerMessage(raw) {
  const parsed = serverMessageSchema.safeParse(raw);
  return parsed.success ? { ok: true, message: parsed.data } : {
    ok: false,
    error: parsed.error.issues.map((i) => `${i.path.join(".")}: ${i.message}`).join("; ")
  };
}
var gameModeSchema = z.enum(["standard", "cross"]);
var gameFormatSchema = z.enum(["2p", "3p", "4p-partners", "4p-ffa"]);
var botDifficultySchema = z.enum(["easy", "medium", "hard", "expert"]);
var rulesetSchema = z.object({
  /**
   * The engine's own targets. 31 is Cross-Cribbage's (`CROSS_TARGET_SCORE`),
   * which Rails records for every Cross game: the grid's rule rather than a
   * host's choice, applied to Cross whatever the engine is sent.
   */
  targetScore: targetScoreSchema.default(121),
  turnSeconds: z.number().int().min(10).max(300).default(45),
  /**
   * How long a dropped seat is held before a bot takes it. Thirty seconds
   * (Mitch, 2026-09-30): the humans still at the table do not wait on a player
   * who left. The player can take the seat back from the bot any time before
   * the game ends.
   */
  reconnectSeconds: z.number().int().min(15).max(600).default(30),
  /**
   * The Cross-Cribbage dealer's crib. Off by default, and the engine refuses it
   * outside Cross, so a lobby that sets it on a classic format is rejected when
   * the room is made rather than played with a silent crib.
   */
  crib: z.boolean().default(false)
});
var connectTokenClaimsSchema = z.object({
  /**
   * Subject: the player id of the seat this token opens.
   *
   * Never a user id — an account is Rails' business and the client never sees
   * one. Bot seats are players too, and they never get a token: the room
   * refuses a token aimed at a seat provisioned as a bot, which is a kind check
   * rather than the null check it used to be.
   */
  sub: z.string().min(1),
  game_id: z.string().uuid(),
  room_id: z.string().min(1),
  seat: seatSchema,
  display_name: z.string().min(1).max(40),
  rating: z.number().int().min(0).max(4e3),
  /**
   * The seat's player is a member, so the room accepts their members' emotes.
   * Read from `Player#premium?` when the token is minted. A token without it
   * is a free seat.
   */
  premium: z.boolean().default(false),
  /** Reserved so adding spectators later is not a breaking change. */
  spectator: z.boolean().default(false),
  jti: z.string().min(1),
  iat: z.number().int(),
  exp: z.number().int(),
  aud: z.literal("cribbage-game-service"),
  iss: z.literal("cribbage-rails")
});
var connectTokenResponseSchema = z.object({
  ws_url: z.string().url(),
  room_id: z.string().min(1),
  token: z.string().min(1),
  seat: seatSchema,
  expires_at: z.string().datetime()
});
var provisionSeatSchema = z.union([
  z.object({
    seat: seatSchema,
    player_id: z.string().uuid(),
    display_name: z.string().min(1).max(40),
    rating: z.number().int().min(0).max(4e3),
    team: z.number().int().min(0).max(3)
  }),
  z.object({
    seat: seatSchema,
    player_id: z.string().uuid(),
    bot: z.literal(true),
    difficulty: botDifficultySchema,
    /** Which build of that difficulty sat down. A snapshot, for the bot lab. */
    bot_version: z.string().min(1),
    display_name: z.string().min(1).max(40),
    team: z.number().int().min(0).max(3)
  })
]);
var provisionRequestSchema = z.object({
  game_id: z.string().uuid(),
  mode: gameModeSchema,
  format: gameFormatSchema,
  ranked: z.boolean(),
  ruleset: rulesetSchema,
  seed: z.string().min(1),
  seats: z.array(provisionSeatSchema).min(2).max(4)
}).superRefine((request, ctx) => {
  const problem = unplayableShape(request.mode, request.format, request.ruleset.crib);
  if (problem !== null) ctx.addIssue({ code: "custom", path: ["format"], message: problem });
});
var provisionResponseSchema = z.object({
  room_id: z.string().min(1),
  ws_url: z.string().url(),
  /** True when the room already existed — provisioning is idempotent. */
  reused: z.boolean().default(false)
});
var gameOutcomeSchema = z.enum([
  ...engineOutcomeSchema.options,
  "abandoned",
  "voided"
]);
var webhookSeatResultSchema = seatResultSchema.extend({
  /** Never null: every seat has a player, bots included. */
  player_id: z.string().uuid(),
  abandoned: z.boolean(),
  /**
   * Defaulted at the ingress, though the engine always sets it.
   *
   * Rails reads this schema at boot and validates before finalizing, and the
   * outbox is strictly ordered and only pops on a 2xx — so a result enqueued
   * by the running game service and delivered after Rails restarts has to
   * still validate, or it and everything behind it stop for good. Requiring a
   * field the service is one deploy away from sending is how that happens.
   * The service always sends it; this default is only for the payload already
   * in Redis when Rails comes up.
   */
  forfeited: z.boolean().default(false)
});
var replayPayloadSchema = z.object({
  format_version: z.number().int().min(1),
  config: gameConfigSchema,
  seed: z.string().min(1),
  moves: z.array(z.object({ seat: seatSchema, move: z.unknown() })),
  /** Full event log; the client replays it directly through AnimationDirector. */
  events: z.array(gameEventSchema)
});
var handRecordSchema = z.object({
  /** 1-based, matching `HAND_STARTED.handNumber`. */
  hand: z.number().int().min(0),
  seat: seatSchema,
  dealer: z.boolean(),
  /** What the hand was worth at the show, even if the game ended mid-count. */
  hand_score: z.number().int().min(0),
  /** Non-zero on the dealer's row only. */
  crib_score: z.number().int().min(0),
  /** Pegged during play, his heels included. */
  pegging_points: z.number().int().min(0)
});
var integrityCountsSchema = z.object({
  rejected_moves: z.number().int().min(0),
  resyncs: z.number().int().min(0),
  timer_expiries: z.number().int().min(0)
});
var tableTalkEntrySchema = z.object({
  seat: seatSchema,
  id: emoteIdSchema,
  at: z.string().datetime()
});
var gameResultWebhookSchema = z.object({
  /** Deduplication key; Rails records it in `processed_webhooks`. */
  event_id: z.string().uuid(),
  game_id: z.string().uuid(),
  outcome: gameOutcomeSchema,
  finished_at: z.string().datetime(),
  results: z.array(webhookSeatResultSchema),
  seed: z.string().min(1),
  engine_version: z.string().min(1),
  replay: replayPayloadSchema.nullable(),
  /** Empty for Cross-Cribbage, which has no show to record. */
  hands: z.array(handRecordSchema).default([]),
  integrity: integrityCountsSchema.default({ rejected_moves: 0, resyncs: 0, timer_expiries: 0 }),
  /** Everything said at the table, in the order the room accepted it. */
  talk: z.array(tableTalkEntrySchema).default([])
});
var webhookAckSchema = z.object({
  status: z.enum(["applied", "duplicate", "ignored"])
});
var recordedMoveSchema = z.object({ seat: seatSchema, move: moveSchema });
var MAX_VERIFIED_MOVES = 2e3;
var verifyRequestSchema = z.object({
  /** Absent on uploads recorded before rules versioning. */
  engine_version: z.string().min(1).optional(),
  config: gameConfigSchema,
  seed: z.string().min(1),
  moves: z.array(recordedMoveSchema).max(MAX_VERIFIED_MOVES),
  /**
   * Accept a game that is legal so far but not over.
   *
   * Off by default, because a caller storing a *result* must not be handed
   * half a game. A caller keeping a running log wants exactly that: the point
   * of logging a game while it is played is being able to look at it before it
   * ends. See `progress` on the response for what an unfinished game reports
   * instead of results.
   */
  allow_unfinished: z.boolean().optional()
});
var seatProgressSchema = z.object({
  seat: seatSchema,
  team: teamSchema,
  score: z.number().int().min(0),
  stats: z.record(z.string(), z.number())
});
var verifyFailureSchema = z.enum([
  /**
   * The log was recorded under rules this engine no longer scores, so it was
   * not replayed at all. A fact about which build is answering rather than
   * about the log, which is why a caller holds it rather than settling it.
   */
  "unsupported_engine_version",
  /** A move in the log is not legal in the position it was played from. */
  "illegal_move",
  /** The log runs out before anybody wins; there is no result to record. */
  "not_finished"
]);
var verifyResponseSchema = z.discriminatedUnion("valid", [
  z.object({
    valid: z.literal(true),
    /**
     * Whether the game is over. Only ever false when the request allowed it,
     * so a caller that did not ask can keep reading this branch as a result.
     */
    finished: z.boolean().default(true),
    mode: gameModeSchema,
    format: gameFormatSchema,
    engine_version: z.string().min(1),
    /**
     * How the game ended, as the re-run says. Defaulted like `finished`, so a
     * verdict on a game still in progress — where `results` is empty — reads as
     * the played-out case rather than making every caller handle a missing key.
     */
    outcome: engineOutcomeSchema.default("completed"),
    /** Derived by re-running the moves, never taken from the uploader. Empty until the game ends. */
    results: z.array(seatResultSchema).default([]),
    /** Where each seat stands right now, finished or not. See `seatProgressSchema`. */
    progress: z.array(seatProgressSchema).default([]),
    hands_played: z.number().int().min(0),
    /** Same per-hand rows a live game reports, folded from the same events. */
    hands: z.array(handRecordSchema).default([])
  }),
  z.object({ valid: z.literal(false), reason: verifyFailureSchema })
]);
var analysisUnitSchema = z.enum(["points", "margin", "equity"]);
var analyzeRequestSchema = z.object({
  /**
   * Absent on replays recorded before rules versioning, exactly as on
   * `verifyRequestSchema`.
   *
   * Grading replays a log under the engine that is deployed, so it has the same
   * exposure verification does: a Cross game recorded before the four-card
   * flush would be priced against rules its players never played under, and the
   * blunders and equity losses stored from it would be about a different game.
   */
  engine_version: z.string().min(1).optional(),
  config: gameConfigSchema,
  seed: z.string().min(1),
  moves: z.array(recordedMoveSchema).max(MAX_VERIFIED_MOVES),
  /** Only these seats are analysed. Analysing a bot's own play is wasted work. */
  seats: z.array(seatSchema),
  /**
   * Also return the per-decision rows, not just the totals.
   *
   * Absent by default because the rollups are all a player's statistics need.
   * The operator replay viewer is what wants the detail: a grid hint and this
   * analysis are the same search, so a stored row is the position's ranking as
   * the player saw it, months later.
   */
  detail: z.boolean().optional(),
  /**
   * Skip the search for moves before this index, and price only what follows.
   *
   * The replay still runs from the first move — reaching a position is the cheap
   * half — but a hard grid search is ~160ms a placement, so re-pricing a whole
   * game every time a device pushes another move is what makes a running log
   * unaffordable. A caller that already stored rows asks for the tail.
   *
   * `summaries` then describe only the graded span, which is why the caller
   * doing this must not fold them into a lifetime total.
   */
  from_index: z.number().int().min(0).optional()
});
var analysisSummarySchema = z.object({
  seat: seatSchema,
  unit: analysisUnitSchema,
  /** Decisions that had a real alternative; a forced move is not a decision. */
  decisions: z.number().int().min(0),
  /** Summed over the estimator's noise floor, so noise cannot become a deficit. */
  total_loss: z.number().min(0),
  /** Decisions that gave up enough to be worth a player's attention. */
  blunders: z.number().int().min(0),
  /** How many of these numbers are game-theoretic rather than estimated. */
  exact_decisions: z.number().int().min(0)
});
var analyzedOptionSchema = z.object({
  move: moveSchema,
  /** Higher is better *for the seat that moved*, in the row's unit. */
  value: z.number()
});
var analyzedCellLinesSchema = z.object({
  cell: z.number().int().min(0),
  row: z.number(),
  column: z.number()
});
var analyzedThrowLinesSchema = z.object({
  next: z.object({ row: z.number(), column: z.number() }),
  crib: z.number(),
  cribAxis: z.enum(["rows", "columns"])
});
var moveAnalysisRecordSchema = z.object({
  /** Index into the request's `moves`, so the row points at the position before it. */
  move_index: z.number().int().min(0),
  seat: seatSchema,
  phase: phaseSchema,
  /** Which hand — or, in Cross-Cribbage, which grid — this belonged to. */
  hand_number: z.number().int().min(0),
  unit: analysisUnitSchema,
  played: moveSchema,
  best: moveSchema,
  /** What the played move gave up, in `unit`. Never negative. */
  loss: z.number().min(0),
  /**
   * Whether `loss` clears `NOISE[unit]` — the same floor the summary rows total
   * against, decided here rather than by whoever reads the row.
   *
   * It is stamped because the readers are in two languages. Rails folds these
   * rows per hand for `hand_records`, and it merges them across grading passes,
   * so it cannot ask the analyzer to total a span it has already stored half of.
   * The alternative was a second copy of the floor in Ruby, and `tuning.ts`
   * exists because that constant had already been copied three times.
   */
  missed: z.boolean(),
  /** True where the numbers are game-theoretic rather than estimated. */
  exact: z.boolean(),
  /** Placements only: values read off the match-equity curve, so at this score. */
  at_score: z.boolean().optional(),
  /** Best first. A grid tops out at 24 open cells, a discard at 15 combinations. */
  options: z.array(analyzedOptionSchema).max(30),
  /**
   * Placements only: the one-card-ahead reason one option beat the other.
   *
   * `played` and `best` are null where the option was the Cross-Cribbage crib
   * rather than a cell, because a thrown card lands on no row and no column.
   * `bestCell` is the top-ranked cell either way, so a review always has a
   * place on the board to hold the crib up against. `crib` is the throw in
   * the cells' own terms, wherever the ranking priced one. All four are
   * defaulted: a record stored before the crib existed carries two cells and
   * no `bestCell`, one stored before the throw had lines carries no `crib`,
   * and both have to keep parsing.
   */
  lines: z.object({
    axis: z.enum(["rows", "columns"]),
    played: analyzedCellLinesSchema.nullable().default(null),
    best: analyzedCellLinesSchema.nullable().default(null),
    bestCell: analyzedCellLinesSchema.nullable().default(null),
    crib: analyzedThrowLinesSchema.nullable().default(null)
  }).optional()
});
var analyzeResponseSchema = z.object({
  /**
   * The engine cannot score this log's rules, so nothing was graded.
   *
   * A separate flag rather than empty summaries, because "priced, and nobody
   * lost anything" and "not priced" must not read the same to a caller that
   * would otherwise stamp the seats and call the game done.
   */
  unsupported: z.boolean().optional(),
  summaries: z.array(analysisSummarySchema),
  /** Present only when the request asked for detail. One row per graded decision. */
  moves: z.array(moveAnalysisRecordSchema).max(MAX_VERIFIED_MOVES).optional()
});
var gameProgressSchema = z.object({
  game_id: z.string().uuid(),
  /** Points per team, indexed by team. */
  scores: z.array(z.number().int().min(0)),
  hand_number: z.number().int().min(0),
  phase: phaseSchema,
  over: z.boolean(),
  /** Per seat: at the table, dropped and held, or played by a bot. */
  seat_status: z.array(seatStatusSchema)
});
var HMAC_HEADER = "x-cribbage-signature";
var HMAC_TIMESTAMP_HEADER = "x-cribbage-timestamp";
var INTERNAL_AUTH_HEADER = "authorization";
var JWT_AUDIENCE = "cribbage-game-service";
var JWT_ISSUER = "cribbage-rails";
var CONNECT_TOKEN_TTL_SECONDS = 300;
var FORMAT_PLAYER_COUNT = {
  "2p": 2,
  "3p": 3,
  "4p-partners": 4,
  "4p-ffa": 4
};
function isPartnersFormat(format) {
  return format === "4p-partners";
}
var PARTNER_GAME_SEATS = [0, 2, 1, 3];
function gameSeatOf(format, seat) {
  return isPartnersFormat(format) ? PARTNER_GAME_SEATS[seat] ?? seat : seat;
}
function unplayableShape(mode, format, crib) {
  if (mode === "cross" && format === "4p-ffa") return "four-player Cross-Cribbage is partners only";
  if (crib && mode !== "cross") return "the dealer\u2019s crib is a Cross-Cribbage option";
  return null;
}
function engineConfigFor(mode, format, ruleset) {
  return {
    variant: mode,
    playerCount: FORMAT_PLAYER_COUNT[format],
    teams: isPartnersFormat(format),
    targetScore: ruleset.targetScore,
    crib: ruleset.crib
  };
}
function formatFor(config) {
  if (config.playerCount === 2) return "2p";
  if (config.playerCount === 3) return "3p";
  return config.teams ? "4p-partners" : "4p-ffa";
}
var uuidSchema = z.string().uuid();
var isoTimeSchema = z.string().datetime();
var DISPLAY_NAME_LENGTH = { min: 2, max: 24 };
var PASSWORD_LENGTH = { min: 8, max: 128 };
var displayNameSchema = z.string().min(DISPLAY_NAME_LENGTH.min).max(DISPLAY_NAME_LENGTH.max);
var newPasswordSchema = z.string().min(PASSWORD_LENGTH.min).max(PASSWORD_LENGTH.max);
var registerRequestSchema = z.object({
  email: z.string().email(),
  password: newPasswordSchema,
  display_name: displayNameSchema
});
var loginRequestSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1)
});
var oauthRequestSchema = z.object({
  provider: z.enum(["apple", "google"]),
  id_token: z.string().min(1),
  display_name: displayNameSchema.optional()
});
var passwordResetRequestSchema = z.object({ email: z.string().email() });
var passwordResetConfirmSchema = z.object({
  token: z.string().min(1),
  password: newPasswordSchema
});
var changeEmailRequestSchema = z.object({
  email: z.string().email(),
  current_password: z.string().min(1)
});
var changePasswordRequestSchema = z.object({
  current_password: z.string().min(1),
  password: newPasswordSchema
});
var sessionSchema = z.object({
  token: z.string().min(1),
  /**
   * Null for a local player. For a guest the session *is* the identity, so a
   * fixed lifetime would orphan anyone who came back after it. Account sessions
   * keep their expiry, where lapsing is the security property and the answer is
   * to sign in again.
   */
  expires_at: isoTimeSchema.nullable(),
  /**
   * The device this session belongs to, or null for a session with no device.
   * Diagnostics only: it grants nothing, and the token is the sole credential.
   */
  device_id: uuidSchema.nullable(),
  user: z.lazy(() => meSchema)
});
var ratingSchema = z.object({
  mode: gameModeSchema,
  format: gameFormatSchema,
  rating: z.number(),
  deviation: z.number(),
  volatility: z.number(),
  games_count: z.number().int().min(0),
  provisional: z.boolean()
});
var globalRatingSchema = ratingSchema.omit({ mode: true, format: true });
var countrySchema = z.string().regex(/^[A-Z]{2}$/);
var avatarIdSchema = z.string().min(1).max(64).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/);
var publicPlayerSchema = z.object({
  id: uuidSchema,
  display_name: z.string(),
  avatar_id: avatarIdSchema.nullable(),
  /** Null when the player has not chosen one, which stays the default. */
  country: countrySchema.nullable(),
  created_at: isoTimeSchema,
  /** Per mode and format, for ladders and matchmaking. Never includes the global row. */
  ratings: z.array(ratingSchema),
  global_rating: globalRatingSchema.nullable(),
  abandon_count: z.number().int().min(0)
});
var relationshipSchema = z.object({
  friend: z.enum(["none", "outgoing", "incoming", "friends"]),
  blocked: z.boolean()
});
var profileSchema = publicPlayerSchema.extend({
  /**
   * True when the player has an account. A guest or a bot cannot be
   * befriended, so the Seat peek draws no Add friend for them.
   */
  account: z.boolean(),
  relationship: relationshipSchema
});
var displayNameModerationStatusSchema = z.enum(["pending", "safe", "unsafe"]);
var styleIdSchema = z.string().regex(/^[a-z0-9-]{1,64}$/);
var playerPreferencesSchema = z.object({
  deck: z.enum(["classic", "four-colour"]),
  card_back: styleIdSchema,
  card_front: styleIdSchema,
  board_finish: styleIdSchema,
  confirm_play: z.boolean(),
  auto_play_forced: z.boolean(),
  bot_speed: z.enum(["instant", "natural", "slow"]),
  /** Which way the player's lines run on their own Cross board. Display only. */
  cross_position: z.enum(["rows", "columns", "flexible"])
}).partial();
var updatePreferencesSchema = z.object({ preferences: playerPreferencesSchema }).strict();
var subscriptionPlanSchema = z.enum(["surface", "comms", "improver", "bundle"]);
var heldPlanSchema = z.enum([...subscriptionPlanSchema.options, "friends_and_family"]);
var subscriptionIntervalSchema = z.enum(["month", "year"]);
var subscriptionStoreSchema = z.enum(["stripe", "apple", "google", "comp"]);
var billedPriceSchema = z.object({
  amount_cents: z.number().int().positive(),
  currency: z.string().length(3)
});
var webPriceSchema = billedPriceSchema.extend({
  id: uuidSchema,
  plan: subscriptionPlanSchema,
  interval: subscriptionIntervalSchema
});
var subscriptionSchema = z.object({
  plan: heldPlanSchema,
  store: subscriptionStoreSchema,
  status: z.enum(["active", "grace", "expired"]),
  interval: subscriptionIntervalSchema,
  renews: z.boolean(),
  /**
   * What a web (Stripe) subscription bills each `interval`. A member keeps it
   * after the plan's price is replaced, until they change plan. Null for a
   * comp, and for Apple and Google, whose apps read the price from the store.
   */
  price: billedPriceSchema.nullable(),
  current_period_ends_at: isoTimeSchema.nullable(),
  grace_ends_at: isoTimeSchema.nullable(),
  ended_at: isoTimeSchema.nullable(),
  /**
   * A downgrade waiting for renewal: the plan switches at `starts_at`, the end
   * of the period already paid for. Null when nothing is waiting.
   */
  pending_change: z.object({
    plan: subscriptionPlanSchema,
    interval: subscriptionIntervalSchema,
    starts_at: isoTimeSchema
  }).nullable()
});
var entitlementsSchema = z.object({
  /** Game Surface: every card front, card back and board. */
  surface: z.boolean(),
  /** Communications Pro: the members-only avatars. */
  comms: z.boolean(),
  /** The Improver: more hints and end-game reviews. */
  improver: z.boolean()
});
var creditsSchema = z.object({
  limit: z.number().int().min(0).nullable(),
  left: z.number().int().min(0).nullable(),
  resets_at: isoTimeSchema
});
var creditKindSchema = z.enum(["hint", "review"]);
var creditSpendSchema = z.object({
  id: uuidSchema,
  kind: creditKindSchema,
  game_key: z.string().min(1).max(100),
  opponents: z.enum(["bots", "people"]),
  spent_at: isoTimeSchema
});
var CREDIT_SPEND_BATCH = 100;
var creditSpendsRequestSchema = z.object({
  spends: z.array(creditSpendSchema).max(CREDIT_SPEND_BATCH)
});
var creditSpendsResponseSchema = z.object({
  rejected: z.array(z.string()),
  credits: creditsSchema
});
var meSchema = publicPlayerSchema.extend({
  display_name_moderation_status: displayNameModerationStatusSchema,
  /**
   * The name this player chose that moderation is still checking, or null.
   * Shown only to its owner. Until it clears, `display_name` stays the
   * approved one, here and everywhere else.
   */
  pending_display_name: z.string().nullable(),
  /**
   * True while this player has no account behind it. An anonymous player has no
   * `email` and no `providers`, cannot choose its own name, and cannot use the
   * account-only surfaces; everything else about the game is open to it.
   */
  anonymous: z.boolean(),
  /** Null for an anonymous player, and for an account that has only OAuth. */
  email: z.string().email().nullable(),
  admin: z.boolean(),
  /** Empty for an anonymous player, and for a password-only account. */
  providers: z.array(z.enum(["apple", "google"])),
  /**
   * When the next rename is allowed, or null if one is allowed now. Computed
   * server-side so the cooldown has exactly one definition; the client only
   * ever compares it to the clock.
   */
  rename_available_at: isoTimeSchema.nullable(),
  /**
   * The server's half of whether to invite this player to learn Cross-Cribbage:
   * ten or more finished regular games, five or more won, no finished Cross
   * game, and the walkthrough never completed. The client still hides the
   * invitation for a game in progress, or a Cross game only the device knows
   * about.
   */
  cross_invitation: z.boolean(),
  /** The Game settings this player last saved, from any device. */
  preferences: playerPreferencesSchema,
  credits: creditsSchema,
  entitlements: entitlementsSchema,
  subscription: subscriptionSchema.nullable(),
  /**
   * What the web store sells now, one price per plan on sale, in the order the
   * plan pages show them. A plan missing here is not sold on the web.
   */
  web_prices: z.array(webPriceSchema)
});
var subscriptionPlanRequestSchema = z.object({
  plan: subscriptionPlanSchema,
  interval: subscriptionIntervalSchema.optional(),
  price: uuidSchema
});
var subscriptionUrlResponseSchema = z.object({ url: z.string().url() });
var importAvailabilitySchema = z.object({
  available: z.boolean(),
  games_count: z.number().int().min(0)
});
var updateMeSchema = z.object({
  display_name: displayNameSchema.optional(),
  avatar_id: avatarIdSchema.nullable().optional(),
  country: countrySchema.nullable().optional()
}).strict();
var gameStatusSchema = z.enum([
  "provisioning",
  "active",
  "completed",
  "forfeited",
  "abandoned",
  "voided"
]);
var participationSchema = z.object({
  seat: seatSchema,
  team: z.number().int().min(0).max(3),
  /** Never null: a bot is a player too, so every seat has one. */
  player_id: uuidSchema,
  /**
   * Whether the seat was a bot. Stated rather than inferred — two bot seats at
   * one table share a player id, and no absent field means "bot" any more.
   */
  bot: z.boolean(),
  display_name: z.string(),
  /**
   * The face the player chose, a catalog key; null for a bot, which wears its
   * level's robot, and for a deleted player. Two seats may share one.
   */
  avatar_id: avatarIdSchema.nullable(),
  /** Which difficulty sat here, or null on a human seat. */
  bot_difficulty: botDifficultySchema.nullable(),
  placement: z.number().int().min(1).max(4).nullable(),
  score: z.number().int().min(0).nullable(),
  abandoned: z.boolean(),
  /** Whether this seat's side ended the game by leaving it. */
  forfeited: z.boolean(),
  /** The per-format move: the format's own row, and the ladder that format ranks. */
  rating_before: z.number().nullable(),
  rating_after: z.number().nullable(),
  rating_delta: z.number().nullable(),
  /**
   * The move on the global rating, the one figure the player surfaces headline
   * (`docs/dev/ratings.md`). Null off a ranked game, on a bot, and until Rails
   * has rated the game.
   */
  global_rating_before: z.number().nullable(),
  global_rating_after: z.number().nullable()
});
var gameSummarySchema = z.object({
  id: uuidSchema,
  mode: gameModeSchema,
  format: gameFormatSchema,
  ranked: z.boolean(),
  status: gameStatusSchema,
  ruleset: rulesetSchema,
  engine_version: z.string(),
  created_at: isoTimeSchema,
  completed_at: isoTimeSchema.nullable(),
  participations: z.array(participationSchema)
});
var activeGameSchema = z.object({
  game: gameSummarySchema,
  progress: gameProgressSchema
});
var gameListSchema = z.object({
  games: z.array(gameSummarySchema),
  next_cursor: z.string().nullable()
});
var replayResponseSchema = z.object({
  game_id: uuidSchema,
  engine_version: z.string(),
  seed: z.string(),
  format_version: z.number().int().min(1),
  /**
   * A replay is `(config, seed, moves)` — that triple is what re-runs the
   * engine and proves it still produces what it produced then. The event log
   * comes too so a viewer can scrub without re-simulating, but the moves are
   * the authoritative record, not a convenience.
   */
  config: gameConfigSchema,
  moves: z.array(z.object({ seat: seatSchema, move: moveSchema })).readonly(),
  events: z.array(gameEventSchema).readonly()
});
var ticketRequestSchema = z.object({
  mode: gameModeSchema,
  format: gameFormatSchema,
  allow_bot_backfill: z.boolean().default(true),
  party_id: uuidSchema.nullish()
});
var ticketStatusSchema = z.enum(["queued", "matched", "cancelled", "expired"]);
var ticketSchema = z.object({
  id: uuidSchema,
  status: ticketStatusSchema,
  mode: gameModeSchema,
  format: gameFormatSchema,
  queued_at: isoTimeSchema,
  /** Present once matched; the client then asks for a connect token. */
  game_id: uuidSchema.nullable(),
  /** Disclosed up front so the client can warn before the queue starts. */
  will_be_ranked: z.boolean(),
  /**
   * Echoed back so the client can tell its own ticket from one Rails handed
   * back. `Enqueue` returns an existing queued ticket and discards the new
   * request's settings, and `will_be_ranked` cannot stand in: before a match it
   * says only whether the shape is rated, true for every unmatched ticket but
   * three-player Cross.
   */
  allow_bot_backfill: z.boolean(),
  /** True once the matched game has a room and seats a bot; the queue names the bot. */
  with_bots: z.boolean(),
  estimated_wait_seconds: z.number().int().min(0).nullable()
});
var LOBBY_TALK_PHRASE_IDS = [
  "hi",
  "ready",
  "one_minute",
  "waiting_on_one",
  "good_luck",
  "lets_play"
];
var LOBBY_TALK_IDS = [
  ...LOBBY_TALK_PHRASE_IDS,
  ...TALK_EMOJI_IDS,
  ...PREMIUM_EMOTE_IDS
];
var lobbyTalkIdSchema = z.enum(LOBBY_TALK_IDS);
var lobbyTalkSchema = z.object({ id: lobbyTalkIdSchema, at: isoTimeSchema });
var lobbyTalkRequestSchema = z.object({ id: lobbyTalkIdSchema });
var lobbyMemberSchema = z.object({
  seat: seatSchema,
  /** Never null. Two seats may share it when both are the same bot. */
  player_id: uuidSchema,
  bot: z.boolean(),
  display_name: z.string(),
  /**
   * The face the player chose, a catalog key; null for a bot, which wears its
   * level's robot, and for a deleted player. Two seats may share one.
   */
  avatar_id: avatarIdSchema.nullable(),
  bot_difficulty: botDifficultySchema.nullable(),
  host: z.boolean(),
  /**
   * The person's rating for this table's game and format, rounded, as the
   * invitation shows it. Null for a bot, and for a person with no games of
   * that kind yet.
   */
  rating: z.number().int().nullable(),
  /** What the seat last said. Null for a bot, and for a person who has said nothing. */
  talk: lobbyTalkSchema.nullable()
});
var lobbyStatusSchema = z.enum(["open", "starting", "started", "closed"]);
var lobbySchema = z.object({
  code: z.string().length(6),
  mode: gameModeSchema,
  format: gameFormatSchema,
  ruleset: rulesetSchema,
  /** The host's player id. A guest may host a private table. */
  host_id: uuidSchema,
  members: z.array(lobbyMemberSchema),
  game_id: uuidSchema.nullable(),
  status: lobbyStatusSchema,
  /**
   * The invitation is past its day (24 hours), so the code seats nobody new.
   * The people already seated keep their seats.
   */
  expired: z.boolean(),
  created_at: isoTimeSchema
});
var lobbyInvitationStateSchema = z.enum([
  "open",
  "seated",
  "started",
  "full",
  "expired",
  "closed"
]);
var lobbyInvitationSeatSchema = z.object({
  seat: seatSchema,
  display_name: z.string(),
  /**
   * The face the player chose, a catalog key; null for a bot, which wears its
   * level's robot, and for a deleted player. Two seats may share one.
   */
  avatar_id: avatarIdSchema.nullable(),
  bot: z.boolean(),
  bot_difficulty: botDifficultySchema.nullable(),
  host: z.boolean(),
  /**
   * The person's rating for this table's game and format, rounded. Null for a
   * bot, and for a person with no games of that kind yet.
   */
  rating: z.number().int().nullable()
});
var lobbyInvitationSchema = z.object({
  code: z.string().length(6),
  mode: gameModeSchema,
  format: gameFormatSchema,
  ruleset: rulesetSchema,
  state: lobbyInvitationStateSchema,
  host_name: z.string(),
  seats: z.array(lobbyInvitationSeatSchema),
  game_id: uuidSchema.nullable()
});
var createLobbySchema = z.object({
  mode: gameModeSchema.default("standard"),
  format: gameFormatSchema.default("2p"),
  ruleset: rulesetSchema.partial().optional()
});
var addBotSchema = z.object({ seat: seatSchema, difficulty: botDifficultySchema });
var friendshipStatusSchema = z.enum(["pending", "accepted"]);
var friendSchema = z.object({
  /**
   * The friend's player id. Friendships are account to account underneath, and
   * the server resolves the player to its user; a request aimed at an anonymous
   * player is refused with `needs_account`.
   */
  player_id: uuidSchema,
  display_name: z.string(),
  avatar_id: avatarIdSchema.nullable(),
  status: friendshipStatusSchema,
  /** True when the other side sent the request and we have not answered. */
  incoming: z.boolean(),
  online: z.boolean()
});
var reportSchema = z.object({
  /** Who is being reported. May be an anonymous player, and so may the reporter. */
  player_id: uuidSchema,
  reason: z.enum(["abuse", "cheating", "name", "other"]),
  detail: z.string().max(1e3).optional(),
  game_id: uuidSchema.nullish()
});
var statEntrySchema = z.object({
  key: z.string(),
  value: z.number(),
  agg: z.enum(["sum", "max", "count"])
});
var statsResponseSchema = z.object({
  mode: gameModeSchema,
  stats: z.array(statEntrySchema),
  /**
   * The last ten finished games under the same filters, oldest first: the Stats
   * screen's form strip. Absent from a server older than the strip.
   */
  recent: z.array(z.object({ at: isoTimeSchema, won: z.boolean() })).optional()
});
var publicStatsResponseSchema = z.object({
  mode: z.enum([...gameModeSchema.options, "all"]),
  stats: z.array(
    statEntrySchema.extend({
      key: z.enum(["games_played", "games_won", "skunks_for", "weighted_wins", "weighted_losses"])
    })
  ),
  recent: z.array(z.object({ won: z.boolean() }))
});
var statsHistorySchema = z.object({
  mode: gameModeSchema,
  format: gameFormatSchema.nullable(),
  window: z.enum(["week", "month", "quarter", "year", "all"]),
  /** One point per finished game, oldest first. */
  games: z.array(
    z.object({
      at: isoTimeSchema,
      won: z.boolean(),
      score: z.number().int().nullable(),
      rating: z.number().nullable()
    })
  ),
  hands: z.object({
    count: z.number().int().min(0),
    mean: z.number(),
    best: z.number().int().min(0),
    as_dealer: z.number(),
    as_pone: z.number(),
    crib_mean: z.number(),
    pegging_mean: z.number(),
    /** Buckets of four, with everything from twenty up sharing the last. */
    distribution: z.array(
      z.object({
        from: z.number().int().min(0),
        to: z.number().int().min(0),
        count: z.number().int().min(0)
      })
    )
  }),
  /**
   * With a format named: one point per ranked game of that format, at the game's
   * time. Without one: the global rating's daily closing values, one point per UTC
   * day it moved, at midnight of that day.
   */
  rating: z.array(z.object({ at: isoTimeSchema, rating: z.number() })),
  /**
   * How many rated games moved that rating inside the window. Without a format,
   * the global rating's own games count since `rating_from`, so a game an
   * import moved in without moving the rating is not counted; with one, the
   * seats behind the line. A daily line has fewer points than games, so the
   * count cannot be read off `rating`. Null, with a null `rating_from`, when
   * the start is unknown. Absent from a server older than the count.
   */
  rated_games: z.number().int().min(0).nullable().optional(),
  /**
   * Where that rating stood before the window: the last daily close before it,
   * a fresh rating's starting value, or with a format the first game's rating
   * before play. The movement over the window is measured from here, since the
   * line's first point is already a rating after play. Null when there is no
   * such rating: no global rating, or no ranked game of the format in the
   * window. Null, with a null `rated_games`, when the start is unknown: a rating
   * whose history began before the snapshot trail and has no close before the
   * window. Absent from a server older than it.
   */
  rating_from: z.number().nullable().optional()
});
var offlineGameUploadSchema = z.object({
  /** Absent on uploads recorded before rules versioning. */
  engine_version: z.string().min(1).optional(),
  /** Generated on the device when the game ended; the server dedupes on it. */
  client_uuid: uuidSchema,
  /**
   * Who played it, stamped when the game was recorded rather than when it is
   * uploaded. That is the whole point of carrying it: a game played signed out
   * used to land on whoever held the token when the sync debounce fired. The
   * server accepts the session's own player, or an anonymous player that has
   * held a session on this device before.
   */
  player_id: uuidSchema,
  format_version: z.number().int().min(1),
  config: gameConfigSchema,
  seed: z.string().min(1),
  moves: z.array(z.object({ seat: seatSchema, move: moveSchema })),
  seat_names: z.array(z.string()),
  /**
   * Per seat, aligned with `seat_names`.
   *
   * Nullable for older clients, but a current one sends a value at every seat
   * including its own: Rails strips nil entries out of an incoming array, which
   * silently shifts a positional list left. The server ignores the uploader's
   * own seat rather than trusting what is there.
   */
  bot_difficulties: z.array(botDifficultySchema.nullable()),
  my_seat: seatSchema,
  started_at: isoTimeSchema.optional(),
  finished_at: isoTimeSchema
});
var offlineSyncResponseSchema = z.object({
  accepted: z.array(uuidSchema),
  /** Already counted — the client should stop offering these too. */
  duplicates: z.array(uuidSchema),
  /** Settled refusals: this log will never verify, so drop it. */
  rejected: z.array(z.object({ client_uuid: uuidSchema, reason: z.string() })),
  /** Unreachable, not refused. Keep these and try again. */
  deferred: z.array(uuidSchema)
});
var leaderboardEntrySchema = z.object({
  rank: z.number().int().min(1),
  /** Always an account-backed player: the leaderboard requires an account. */
  player_id: uuidSchema,
  display_name: z.string(),
  rating: z.number(),
  games_count: z.number().int()
});
var leaderboardSchema = z.object({
  mode: gameModeSchema,
  format: gameFormatSchema,
  entries: z.array(leaderboardEntrySchema)
});
var presenceSchema = z.object({
  online: z.number().int().min(0)
});
var platformSchema = z.enum(["ios", "android", "web"]);
var registerDeviceSchema = z.object({ platform: platformSchema });
var devicePushTokenSchema = z.object({
  platform: platformSchema,
  token: z.string().min(1)
});
var apiErrorSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.record(z.string(), z.array(z.string())).optional()
  })
});

// src/tuning.ts
var BOT_BUDGETS = { crossLevel: "deep" };
var NOISE = {
  points: 0.25,
  margin: 0.5,
  equity: 5e-3
};
var BLUNDER = {
  points: 2,
  margin: 3,
  equity: 0.05
};
function botSeed(seed, seat) {
  return `${seed}|bot|${seat}`;
}
var TALK_COOLDOWN_MS = 2500;

export { BLUNDER, BOT_BUDGETS, CONNECT_TOKEN_TTL_SECONDS, CREDIT_SPEND_BATCH, DISPLAY_NAME_LENGTH, EMOTE_IDS, FORMAT_PLAYER_COUNT, HMAC_HEADER, HMAC_TIMESTAMP_HEADER, INTERNAL_AUTH_HEADER, JWT_AUDIENCE, JWT_ISSUER, LOBBY_TALK_IDS, LOBBY_TALK_PHRASE_IDS, MAX_VERIFIED_MOVES, NOISE, PASSWORD_LENGTH, PREMIUM_EMOTE_IDS, PROTOCOL_VERSION, REPLAY_FORMAT_VERSION, TALK_COOLDOWN_MS, TALK_EMOJI_IDS, TALK_PHRASE_IDS, activeGameSchema, addBotSchema, analysisSummarySchema, analysisUnitSchema, analyzeRequestSchema, analyzeResponseSchema, analyzedCellLinesSchema, analyzedOptionSchema, analyzedThrowLinesSchema, apiErrorSchema, avatarIdSchema, billedPriceSchema, botDifficultySchema, botSeed, c2sEmoteSchema, c2sMoveSchema, c2sPingSchema, c2sReadySchema, c2sReclaimSeatSchema, c2sResyncSchema, cardSchema, changeEmailRequestSchema, changePasswordRequestSchema, clientMessageSchema, connectTokenClaimsSchema, connectTokenResponseSchema, countrySchema, createLobbySchema, creditKindSchema, creditSpendSchema, creditSpendsRequestSchema, creditSpendsResponseSchema, creditsSchema, crossLineSchema, crossStateSchema, currentJoinOptions, devicePushTokenSchema, displayNameModerationStatusSchema, emoteIdSchema, engineConfigFor, engineOutcomeSchema, entitlementsSchema, errorCodeSchema, formatFor, friendSchema, friendshipStatusSchema, gameConfigSchema, gameEventSchema, gameFormatSchema, gameListSchema, gameModeSchema, gameOutcomeSchema, gameProgressSchema, gameResultSchema, gameResultWebhookSchema, gameSeatOf, gameStatusSchema, gameSummarySchema, globalRatingSchema, handRecordSchema, heldPlanSchema, importAvailabilitySchema, integrityCountsSchema, isPartnersFormat, isPremiumEmote, isoTimeSchema, joinOptionsSchema, leaderboardEntrySchema, leaderboardSchema, lobbyInvitationSchema, lobbyInvitationSeatSchema, lobbyInvitationStateSchema, lobbyMemberSchema, lobbySchema, lobbyStatusSchema, lobbyTalkIdSchema, lobbyTalkRequestSchema, lobbyTalkSchema, loginRequestSchema, meSchema, moveAnalysisRecordSchema, moveSchema, oauthRequestSchema, offlineGameUploadSchema, offlineSyncResponseSchema, opponentViewSchema, parseClientMessage, parseServerMessage, participationSchema, passwordResetConfirmSchema, passwordResetRequestSchema, phaseSchema, platformSchema, playedCardSchema, playerCountSchema, playerPreferencesSchema, presenceSchema, profileSchema, provisionRequestSchema, provisionResponseSchema, provisionSeatSchema, publicStatsResponseSchema, ratingSchema, recordedMoveSchema, redactedPeggingSchema, redactedStateSchema, registerDeviceSchema, registerRequestSchema, relationshipSchema, replayPayloadSchema, replayResponseSchema, reportSchema, rulesetSchema, s2cEmoteSchema, s2cErrorSchema, s2cEventsSchema, s2cGameOverSchema, s2cMoveRejectedSchema, s2cPongSchema, s2cSeatStatusSchema, s2cSnapshotSchema, s2cTimerSchema, scoreItemSchema, scoreReasonSchema, scoreSourceSchema, seatProgressSchema, seatResultSchema, seatSchema, seatStatusSchema, serverMessageSchema, sessionSchema, statEntrySchema, statsHistorySchema, statsResponseSchema, subscriptionIntervalSchema, subscriptionPlanRequestSchema, subscriptionPlanSchema, subscriptionSchema, subscriptionStoreSchema, subscriptionUrlResponseSchema, tableTalkEntrySchema, targetScoreSchema, teamSchema, ticketRequestSchema, ticketSchema, ticketStatusSchema, unplayableShape, updateMeSchema, updatePreferencesSchema, uuidSchema, variantSchema, verifyFailureSchema, verifyRequestSchema, verifyResponseSchema, viewerSchema, webPriceSchema, webhookAckSchema, webhookSeatResultSchema };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map